import { useState, useEffect, useMemo } from "react";
import constants from "../../../../constants";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import {
  appointmentCancelNextVisit,
  appointmentDetailActions,
  reviewComment,
} from "repositories/patient-appointment-repository";
import { useQueryClient } from "react-query";
import moment from "moment";
import { getStorage, setStorage } from "utils";

const defaultDate = {
  from: moment().startOf("year").toDate(),
  to: moment().toDate(),
};

const pageSize = 10;
const useUpCommingModule = (
  useAllAppointments,
  state,
  currentTab,
  isRecall = false
) => {
  let appointmentFilter = {
    upcoming: constants.appointmentList.cache.upCommingfilters,
    recall: constants.appointmentList.cache.recallfilters,
    past: constants.appointmentList.cache.pastfilters,
  };

  const globalFilterKey = appointmentFilter[currentTab];
  const officeSpecificFilterKey = `${appointmentFilter[currentTab]}_office_${
    state?.officeId || "global"
  }`;
  const cacheFilters =
    getStorage(state?.officeId ? officeSpecificFilterKey : globalFilterKey) ||
    {};
  const isFilterApply =
    cacheFilters?.offices?.length > 0 ||
    cacheFilters?.statuses?.length > 0 ||
    cacheFilters?.doctors?.length > 0;
  const profile = useSelector((state) => state.userProfile.profile);
  const [pageNumber, setPageNumber] = useState(1);
  const [appliedOfficeFilters, setAppliedOfficeFilters] = useState(
    cacheFilters?.offices || []
  );
  const [appliedDocotorFilters, setAppliedDoctorFilters] = useState(
    cacheFilters?.doctors || []
  );
  const [searchValue, setSearchValue] = useState("");
  const [appliedAppointmentStatusFilters, setSppliedAppointmentStatusFilters] =
    useState(cacheFilters?.statuses || []);
  const [appliedRecallTypeFilters, setAppliedRecallTypeFilters] = useState(
    cacheFilters?.recallType || []
  );
  const [appointments, setAppointments] = useState([]);
  const [officeFilter, setOfficeFilter] = useState(cacheFilters?.offices || []);
  const [doctorFilter, setDocotrFilter] = useState(cacheFilters?.doctors || []);
  const [statusFilter, setStatusFilter] = useState(
    cacheFilters?.statuses || []
  );

  const [recallTypeFilter, setRecallTypeFilter] = useState(
    cacheFilters?.recallType || []
  );
  const [totalItems, setTotalItems] = useState(0);
  const [showLoader, setShowLoader] = useState(false);
  const [dates, setdates] = useState(defaultDate);
  const [utcdates, setUtcdates] = useState({ from: null, to: null });
  const queryClient = useQueryClient();
  let params = {
    pageNumber: pageNumber,
    pageSize: pageSize,
    doctorId: profile?.id,
    fromDate: utcdates.from,
    toDate: utcdates.to,
    officeIds: appliedOfficeFilters,
    appointmentStatus: appliedAppointmentStatusFilters,
    searchTerm: searchValue,
  };
  if (state?.officeId && !state?.patientId) {
    params.officeId = state?.officeId;
    params.doctorIds = appliedDocotorFilters;
    params.isAdmin = false;
    delete params.doctorId;
    delete params.officeIds;
  }
  if (state?.patientId) {
    params.patientId = +state?.patientId;
    params.officeId = +state?.officeId;
    delete params.fromDate;
    delete params.toDate;
    delete params.officeIds;
  }
  if (state?.isAdmin) {
    params.isAdmin = true;
  }

  if (isRecall) {
    delete params.appointmentStatus;
    delete params.fromDate;
    delete params.toDate;
    params.recallStatus = appliedAppointmentStatusFilters;
    params.recallTypeIds = appliedRecallTypeFilters;
  }

  const { isLoading, data, refetch } = useAllAppointments(params);

  /** Status IDs that are valid for the current tab (recall vs upcoming vs past). */
  const allowedRouteStatusIds = useMemo(() => {
    if (isRecall || currentTab === "recall") {
      return new Set(constants.recallAppointmentStatus.map((s) => s.id));
    }
    if (currentTab === "upcoming") {
      return new Set(constants.upComingAppointmentStatus.map((s) => s.id));
    }
    if (currentTab === "past") {
      return new Set(constants.pastAppointmentStatus.map((s) => s.id));
    }
    return new Set();
  }, [currentTab, isRecall]);

  /**
   * When the user lands from the appointment dashboard (or similar) with
   * `location.state.statusFilter`, keep filters in sync on every navigation —
   * not only the first visit (the old isTabClicked gate blocked Overdue → Converted).
   */
  const routeStatusFilterKey = useMemo(() => {
    const filters = state?.statusFilter;
    if (!Array.isArray(filters) || !filters.length) return "";
    if (!allowedRouteStatusIds.size) return "";
    const ids = filters.map(Number);
    if (!ids.every((id) => allowedRouteStatusIds.has(id))) return "";
    return ids.slice().sort((a, b) => a - b).join(",");
  }, [state?.statusFilter, allowedRouteStatusIds]);

  useEffect(() => {
    if (!routeStatusFilterKey) return;
    const nextStatuses = routeStatusFilterKey.split(",").map(Number);
    setStatusFilter(nextStatuses);
    setSppliedAppointmentStatusFilters(nextStatuses);
    const filterStorageKey = state?.officeId
      ? officeSpecificFilterKey
      : globalFilterKey;
    const prior = getStorage(filterStorageKey) || {};
    setStorage(filterStorageKey, {
      ...prior,
      statuses: nextStatuses,
    });
    setStorage(constants.appointmentList.cache.isTabClicked, {
      isTabClicekd: true,
    });
  }, [
    routeStatusFilterKey,
    state?.officeId,
    officeSpecificFilterKey,
    globalFilterKey,
  ]);

  useEffect(() => {
    if (!isLoading && data?.items) {
      setAppointments([...data?.items]);
      setTotalItems(data?.pagination?.totalItems);
    }
  }, [isLoading, data]);

  const handleInputChange = (e) => {
    setSearchValue(e.target.value);
  };

  const appointmentDetailAction = async (params) => {
    setShowLoader(true);
    try {
      let res = await appointmentDetailActions(params);
      if (res?.statusCode === 200) {
        setShowLoader(false);
        toast.success(res.message);
        queryClient.invalidateQueries(["/Appointment/Upcoming", params]);
        refetch();
      }
      return res;
    } catch (err) {
      setShowLoader(false);
      toast.error(err.message);
      return { status: false };
    }
  };

  const cancelNextRecallDate = async (AppointmentRecallId, RecallDate) => {
    setShowLoader(true);
    try {
      let res = await appointmentCancelNextVisit(
        AppointmentRecallId,
        RecallDate
      );
      if (res?.statusCode === 200) {
        setShowLoader(false);
        toast.success(res.message);
        queryClient.invalidateQueries(["/Appointment/Upcoming", params]);
        refetch();
      }
      return res;
    } catch (err) {
      setShowLoader(false);
      toast.error(err.message);
      return { status: false };
    }
  };

  const addReviewComment = async (params) => {
    setShowLoader(true);
    try {
      let res = await reviewComment(params);
      if (res?.statusCode === 200) {
        setShowLoader(false);
        toast.success(res.message);
        queryClient.invalidateQueries(["/Appointment/Upcoming", params]);
        refetch();
      }
      return res;
    } catch (err) {
      setShowLoader(false);
      toast.error(err.message);
      return { status: false };
    }
  };

  const handleApplyFilters = () => {
    setAppliedOfficeFilters(officeFilter);
    setSppliedAppointmentStatusFilters(statusFilter);
    setAppliedRecallTypeFilters(recallTypeFilter);
    setAppliedDoctorFilters(doctorFilter);

    setStorage(state?.officeId ? officeSpecificFilterKey : globalFilterKey, {
      offices: officeFilter,
      statuses: statusFilter,
      recallType: recallTypeFilter,
      doctors: doctorFilter,
    });
    setPageNumber(1);
  };

  const resetFilter = () => {
    setPageNumber(1);
    setAppliedOfficeFilters([]);
    setSppliedAppointmentStatusFilters([]);
    setAppliedRecallTypeFilters([]);
    setAppliedDoctorFilters([]);
    setOfficeFilter([]);
    setStatusFilter([]);
    setRecallTypeFilter([]);
    setDocotrFilter([]);
    setStorage(state?.officeId ? officeSpecificFilterKey : globalFilterKey, {
      offices: [],
      statuses: [],
      recallType: [],
      doctors: [],
    });
    setdates(defaultDate);
    setUtcdates({ from: null, to: null });
    queryClient.invalidateQueries(["/Appointment/Upcoming", params]);
    refetch();
  };

  const updateDatesValues = (e, type) => {
    const currentDate = moment().utc().local().format("YYYY-MM-DD");
    const yearStartDate = moment()
      .startOf("year")
      .utc()
      .local()
      .format("YYYY-MM-DD");
    setdates((prevDates) => ({
      ...prevDates,
      [type]: e,
    }));
    setUtcdates((prevState) => ({
      ...prevState,
      from:
        type === "from"
          ? moment.utc(e).local().format("YYYY-MM-DD")
          : prevState.from || yearStartDate,
      to:
        type === "to"
          ? moment.utc(e).local().format("YYYY-MM-DD")
          : prevState.to || currentDate,
    }));
  };

  return {
    data: {
      isLoading,
      totalItems,
      pageNumber,
      appointments,
      showLoader,
      searchValue,
      officeFilter,
      statusFilter,
      dates,
      recallTypeFilter,
      isFilterApply,
      doctorFilter,
    },
    methods: {
      setPageNumber,
      appointmentDetailAction,
      handleInputChange,
      setOfficeFilter,
      handleApplyFilters,
      resetFilter,
      setStatusFilter,
      updateDatesValues,
      setRecallTypeFilter,
      cancelNextRecallDate,
      setDocotrFilter,
      addReviewComment,
    },
  };
};

export default useUpCommingModule;
